import { useEffect, useState } from 'react';
import { Dropdown, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import Sentry from 'src/services/sentry';

import { Voice } from 'src/models/Camera/Settings/Language/GetLanguages.model';
import { SetLanguageType } from 'src/models/Camera/Settings/Language/SetLanguage.model';
import { I18NextNS } from 'src/models/Language.model';

import { useGetCameraLanguages } from 'src/hooks/camera/useGetCameraLanguages';
import { useGetCameraSettings } from 'src/hooks/camera/useGetCameraSettings';
import { useSetCameraLanguage } from 'src/hooks/camera/useSetCameraLanguage';

import { captureError } from 'src/utils/errorReporting';
const CameraLanguageDropdown = () => {
  const { t: tPages } = useTranslation(I18NextNS.PAGES);

  const { languages, getCameraLanguages } = useGetCameraLanguages();

  const {
    cameraSettings,
    getCameraSettings,
    setCameraSettings,
    isGettingCameraSettings,
  } = useGetCameraSettings();
  const { setCameraLanguage } = useSetCameraLanguage();

  useEffect(() => {
    getCameraLanguages();
    getCameraSettings();
  }, [getCameraLanguages, getCameraSettings]);

  const [isUpdatingLanguage, setIsUpdatingLanguage] = useState(false);

  const handleSetCameraLanguage = async (lang: Voice) => {
    try {
      if (!cameraSettings) {
        Sentry.captureMessage('Camera settings not found');
        return;
      }

      setIsUpdatingLanguage(true);
      await setCameraLanguage({
        lang: lang.value,
        type: SetLanguageType.Voice,
      });

      setCameraSettings({
        ...cameraSettings,
        obj: {
          ...cameraSettings?.obj,
          settings: {
            ...cameraSettings?.obj.settings,
            voice: lang.value,
          },
        },
      });
    } catch (error) {
      captureError(error, 'Failed to set camera language');
      // TODO: Uncomment this after we receive the error message from the translation company
      // Api.alertBox({
      //   title: t('bootbox.title.Error', { ns: I18NextNS.COMMON }),
      //   message: t('message.error.failedToSetCameraLanguage', {
      //     ns: I18NextNS.COMMON,
      //   }),
      //   locale: language,
      //   type: 'error',
      // });
    } finally {
      setIsUpdatingLanguage(false);
    }
  };
  return (
    <Dropdown>
      <Dropdown.Toggle variant="primary-100">
        {isUpdatingLanguage || isGettingCameraSettings ? (
          <Spinner size="sm" className="me-2" />
        ) : (
          <>
            <strong>{tPages('newExam.cameraLanguage')}</strong>{' '}
            {(() => {
              const foundLanguage = languages?.obj.voice.find(
                (lang: Voice) =>
                  lang.value === cameraSettings?.obj.settings.voice,
              )?.value;
              return (
                foundLanguage &&
                tPages(`newExam.cameraLanguageDropdownItems.${foundLanguage}`)
              );
            })()}
          </>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu>
        {languages?.obj.voice.map((lang: Voice) => (
          <Dropdown.Item
            key={lang.label}
            active={lang.value === cameraSettings?.obj.settings.voice}
            onClick={() => handleSetCameraLanguage(lang)}
          >
            {tPages(`newExam.cameraLanguageDropdownItems.${lang.value}`)}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default CameraLanguageDropdown;
